import { useState, useRef, useEffect } from "react";

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    id: "free",
    question: "Is this actually free?",
    answer: (
      <div className="flex flex-col gap-4">
        <p style={{ color: "#4A4A4A", fontFamily: "'Inter', sans-serif", lineHeight: "1.75" }}>
          Yes — completely free, no strings attached. Here's exactly what happens on the call:
        </p>
        <ul className="flex flex-col gap-3">
          {[
            "I review your current online presence Google profile, website, and social ads if any.",
            "I identify your biggest client acquisition gap (the thing quietly costing you bookings every month).",
            "I give you a simple projection of what a well-run campaign could realistically generate for you based on your area, services, and pricing.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div
                className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
                style={{ background: "rgba(201, 149, 111, 0.15)", border: "1px solid rgba(201, 149, 111, 0.4)" }}
              >
                <i className="ri-check-line text-xs" style={{ color: "#C9956F" }} />
              </div>
              <span
                className="text-sm leading-relaxed"
                style={{ color: "#4A4A4A", fontFamily: "'Inter', sans-serif" }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: "after",
    question: "What happens after the audit?",
    answer: (
      <div className="flex flex-col gap-3">
        <p style={{ color: "#4A4A4A", fontFamily: "'Inter', sans-serif", lineHeight: "1.75" }}>
          I&apos;ll be straight with you. I value honesty above everything else, so let me be upfront:
          at the end of the call I may offer for us to work together, if I genuinely believe I can
          help you acquire more clients and the fit feels right.
        </p>
        <p style={{ color: "#4A4A4A", fontFamily: "'Inter', sans-serif", lineHeight: "1.75" }}>
          But there&apos;s absolutely zero obligation, and I&apos;m not here to fake altruism either. Truth
          is: I gain a lot from analyzing your business and thinking through your growth, even if we
          never work together. So this isn&apos;t charity; it&apos;s a conversation between two people who
          might benefit from knowing each other.
        </p>
        <p
          className="text-sm font-medium px-4 py-3 rounded-md"
          style={{
            color: "#7A5A3A",
            background: "rgba(201, 149, 111, 0.08)",
            border: "1px solid rgba(201, 149, 111, 0.2)",
            fontFamily: "'Inter', sans-serif",
            lineHeight: "1.65",
          }}
        >
          No pressure. No pitch deck. No manipulation. Just an honest conversation about your business.
        </p>
      </div>
    ),
  },
];

function FAQAccordionItem({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, []);

  const toggle = () => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
    setOpen((prev) => !prev);
  };

  return (
    <div
      className="rounded-lg overflow-hidden cursor-pointer"
      style={{ border: "1px solid #EDEDE9", background: "#fff" }}
      onClick={toggle}
    >
      <div className="flex items-center justify-between gap-4 px-7 py-5">
        <h3
          className="text-base md:text-lg font-semibold"
          style={{ fontFamily: "'Playfair Display', serif", color: "#1C1C1C" }}
        >
          {item.question}
        </h3>
        <div
          className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300"
          style={{
            background: open ? "rgba(201, 149, 111, 0.12)" : "#F5F2EE",
            border: `1px solid ${open ? "rgba(201, 149, 111, 0.35)" : "#E8E5E0"}`,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <i
            className="ri-arrow-down-s-line text-base"
            style={{ color: open ? "#C9956F" : "#9A9A9A" }}
          />
        </div>
      </div>

      <div
        ref={contentRef}
        style={{
          maxHeight: open ? (height === "auto" ? "auto" : `${height}px`) : "0px",
          overflow: "hidden",
          transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          className="px-7 pb-6"
          style={{ borderTop: "1px solid #F5F2EE" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pt-5">{item.answer}</div>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 px-6"
      style={{ background: "#F9F7F4" }}
    >
      <div
        className="max-w-2xl mx-auto"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(28px)",
          transition: "opacity 0.8s ease, transform 0.8s ease",
        }}
      >
        {/* Header */}
        <div className="text-center mb-10">
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-4 px-4 py-1.5 rounded-full"
            style={{
              background: "rgba(201, 149, 111, 0.1)",
              color: "#C9956F",
              border: "1px solid rgba(201, 149, 111, 0.25)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Common Questions
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold"
            style={{ fontFamily: "'Playfair Display', serif", color: "#1C1C1C", letterSpacing: "-0.01em" }}
          >
            Let's Be Transparent
          </h2>
          <p
            className="text-base mt-3"
            style={{ color: "#7A7A7A", fontFamily: "'Inter', sans-serif" }}
          >
            A couple of things you're probably wondering right now.
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-4">
          {faqs.map((faq) => (
            <FAQAccordionItem key={faq.id} item={faq} />
          ))}
        </div>
      </div>
    </section>
  );
}
